/**
 * The price grid — several suppliers' prices for many lines, typed in one pass, saved in one go.
 * See `lib/quoteGrid.ts` for the shape and the reasoning; this is the keyboard.
 *
 * Columns are suppliers — the ones already in play, plus any picked or created here (an RFQ is
 * not a precondition). Each column has its own currency; the date is one for the batch. Rows
 * default to the lines nobody has priced yet, because on a hundred-line enquiry that is the
 * work; the toggle shows them all. A cell with a figure a supplier already quoted shows it
 * greyed, so what is being typed is typed against the competition.
 *
 * Keyboard: Enter moves down a column, Tab moves across, as a purchaser reading a supplier's
 * list top to bottom expects. No paste-a-message field (Akhil, 2026-09-17: "it will complicate
 * things") — typing into cells is the whole interaction.
 */

import { useMemo, useState } from 'react'
import { LineTextarea } from '../../components/LineTextarea'
import { Modal } from '../../components/Modal'
import { Button } from '../../components/ui'
import { LinkCombo } from '../../components/LinkPicker'
import { SearchBox } from '../../components/SearchBox'
import { useToast } from '../../components/Toast'
import { useCurrencyPicker } from '../../lib/api/useEnquiryFromMail'
import { useCreateSupplier, useSuppliers } from '../../lib/api/useSuppliers'
import { entriesFromCells, gridColumns, gridRows, type GridEntry, type GridRow } from '../../lib/quoteGrid'
import { formatMoney } from '../../lib/format'
import type { EnquiryItem, Rfq, SupplierQuotation } from '../../lib/types'

const cellStyle: React.CSSProperties = { width: 96, textAlign: 'right', fontSize: 12.5, padding: '3px 6px' }

export function QuoteGridModal({
  lines,
  quotations,
  rfqs,
  nameByCode,
  supplierNames,
  baseCurrency,
  company,
  saving,
  onClose,
  onSave,
}: {
  lines: EnquiryItem[]
  quotations: SupplierQuotation[]
  rfqs: Rfq[]
  nameByCode: ReadonlyMap<string, string>
  supplierNames: ReadonlyMap<string, string>
  baseCurrency: string
  company: string
  saving: boolean
  onClose: () => void
  onSave: (entries: GridEntry[], transactionDate: string) => void
}) {
  const toast = useToast()
  const [extraColumns, setExtraColumns] = useState<string[]>([])
  const [cells, setCells] = useState<Map<string, Map<string, number>>>(new Map())
  const [text, setText] = useState<Map<string, string>>(new Map())
  const [currencyByColumn, setCurrencyByColumn] = useState<Map<string, string>>(new Map())
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [unquotedOnly, setUnquotedOnly] = useState(true)
  const [search, setSearch] = useState('')
  const [addingColumn, setAddingColumn] = useState('')
  // Offer remarks per cell (2026-09-18) — "MOQ 5", "ex-stock" — filed with the rate beside them.
  const [remarks, setRemarks] = useState<Map<string, Map<string, string>>>(new Map())
  const setRemark = (column: string, lineId: string, value: string) =>
    setRemarks((prev) => {
      const next = new Map(prev)
      const col = new Map(next.get(column) ?? [])
      if (value.trim() === '') col.delete(lineId)
      else col.set(lineId, value)
      next.set(column, col)
      return next
    })

  const rows = useMemo(() => gridRows(lines, quotations, rfqs, nameByCode), [lines, quotations, rfqs, nameByCode])
  const columns = useMemo(() => gridColumns(lines, quotations, rfqs, extraColumns), [lines, quotations, rfqs, extraColumns])
  const term = search.trim().toLowerCase()
  const shown = rows.filter((r) => {
    if (unquotedOnly && Object.keys(r.quoted).length > 0 && !columns.some((c) => cells.get(c)?.has(r.line_id))) return false
    if (term === '' ) return true
    return r.cust_sno === term || r.item_code.toLowerCase().includes(term) || r.name.toLowerCase().includes(term) || r.master.toLowerCase().includes(term)
  })

  const useScopedSuppliers = (q: string) => {
    const query = useSuppliers(q, company)
    return { data: query.data?.data.map((s) => ({ name: s.code, label: s.name })), isPending: query.isPending }
  }
  const createSupplier = useCreateSupplier({
    onSuccess: (s) => {
      toast(`Supplier created: ${s.name}`)
      setExtraColumns((c) => [...new Set([...c, s.code])])
    },
  })

  const currencyOf = (column: string) => currencyByColumn.get(column) ?? baseCurrency
  const keyOf = (column: string, lineId: string) => `${column} ${lineId}`

  const commit = (column: string, lineId: string, raw: string) => {
    const trimmed = raw.trim()
    setCells((prev) => {
      const next = new Map(prev)
      const col = new Map(next.get(column) ?? [])
      const n = Number(trimmed.replace(',', '.'))
      if (trimmed === '' || !Number.isFinite(n) || n < 0) col.delete(lineId)
      else col.set(lineId, n)
      next.set(column, col)
      return next
    })
  }

  const entries = entriesFromCells(cells, new Map(columns.map((c) => [c, currencyOf(c)])), remarks)
  const filledCount = entries.reduce((n, e) => n + e.rates.size, 0)

  const move = (e: React.KeyboardEvent<HTMLInputElement>, column: string, rowIndex: number) => {
    const colIndex = columns.indexOf(column)
    let target: [number, number] | null = null
    if (e.key === 'Enter') target = [colIndex, rowIndex + (e.shiftKey ? -1 : 1)]
    if (e.key === 'ArrowDown') target = [colIndex, rowIndex + 1]
    if (e.key === 'ArrowUp') target = [colIndex, rowIndex - 1]
    if (!target) return
    e.preventDefault()
    const [c, r] = target
    const next = document.querySelector<HTMLInputElement>(`input[data-cell="${c}:${r}"]`)
    next?.focus()
    next?.select()
  }

  return (
    <Modal
      title="Record supplier prices — grid"
      onClose={onClose}
      width={Math.min(1400, 560 + columns.length * 120)}
      footer={
        <>
          <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>
            {filledCount} price(s) across {entries.length} supplier(s) — one quotation per supplier
          </span>
          <span style={{ flex: 1 }} />
          <Button variant="primary" disabled={saving || entries.length === 0} onClick={() => onSave(entries, date)}>
            {saving ? 'Filing…' : `File ${entries.length} quotation(s)`}
          </Button>
        </>
      }
    >
      <div className="pad" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', borderBottom: '1px solid var(--line)' }}>
        <label className="ef" style={{ width: 150 }}>
          <span>Quotation date</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="ef" style={{ minWidth: 260 }}>
          <span>Add a supplier column — any supplier, RFQ or not</span>
          <LinkCombo
            value={addingColumn}
            useOptions={useScopedSuppliers}
            placeholder="Search suppliers…"
            onChange={(code) => {
              if (code) setExtraColumns((c) => [...new Set([...c, code])])
              setAddingColumn('')
            }}
          />
        </label>
        <NewSupplierInline creating={createSupplier.isPending} onCreate={(name, email) => createSupplier.mutate({ name, email })} />
        <label style={{ fontSize: 12.5, display: 'flex', gap: 6, alignItems: 'center', marginLeft: 'auto' }}>
          <input type="checkbox" checked={unquotedOnly} onChange={(e) => setUnquotedOnly(e.target.checked)} /> unpriced lines only
        </label>
        <SearchBox value={search} onChange={setSearch} total={rows.length} shown={shown.length} placeholder="serial, code or name…" style={{ width: 220 }} />
      </div>

      <div style={{ overflow: 'auto', maxHeight: '60vh' }}>
        <table style={{ width: '100%' }}>
          <thead style={{ position: 'sticky', top: 0, background: 'var(--panel)', zIndex: 1 }}>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Item</th>
              <th className="right">Qty</th>
              {columns.map((c) => (
                <th key={c} style={{ minWidth: 110 }}>
                  <div style={{ fontWeight: 700 }}>{supplierNames.get(c) ?? c}</div>
                  <div style={{ marginTop: 3 }}>
                    <CurrencyCell value={currencyOf(c)} onChange={(cur) => setCurrencyByColumn((m) => new Map(m).set(c, cur))} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr>
                <td colSpan={3 + columns.length} style={{ color: 'var(--muted)', textAlign: 'center', padding: 18 }}>
                  {rows.length === 0 ? 'No RFQ-routed, matched lines to price.' : unquotedOnly ? 'Every line has a price — untick "unpriced lines only" to see them all.' : 'Nothing matches the search.'}
                </td>
              </tr>
            ) : null}
            {shown.map((row, rowIndex) => (
              <GridLine
                key={row.line_id}
                row={row}
                rowIndex={rowIndex}
                columns={columns}
                text={text}
                keyOf={keyOf}
                setText={setText}
                commit={commit}
                move={move}
                remarks={remarks}
                setRemark={setRemark}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  )
}

function GridLine({
  row,
  rowIndex,
  columns,
  text,
  keyOf,
  setText,
  commit,
  move,
  remarks,
  setRemark,
}: {
  row: GridRow
  rowIndex: number
  columns: string[]
  text: Map<string, string>
  keyOf: (column: string, lineId: string) => string
  setText: React.Dispatch<React.SetStateAction<Map<string, string>>>
  commit: (column: string, lineId: string, raw: string) => void
  move: (e: React.KeyboardEvent<HTMLInputElement>, column: string, rowIndex: number) => void
  remarks: Map<string, Map<string, string>>
  setRemark: (column: string, lineId: string, value: string) => void
}) {
  return (
    <tr>
      <td className="mono">{row.cust_sno}</td>
      <td style={{ whiteSpace: 'normal' }}>
        {/* The line as the purchaser wrote it — the description that prints for the supplier —
            with the catalogue's name beneath when it differs (Akhil, 2026-10-16). */}
        <div>{row.name}</div>
        {row.master && row.master !== row.name ? <div style={{ fontSize: 11, color: 'var(--muted)' }}>{row.master}</div> : null}
        <div style={{ fontSize: 11, color: 'var(--muted)' }} className="mono">
          {row.item_code} · {row.uom}
        </div>
      </td>
      <td className="right mono">{row.qty}</td>
      {columns.map((c, colIndex) => {
        const key = keyOf(c, row.line_id)
        const already = row.quoted[c]
        const sent = row.sentTo.has(c)
        return (
          <td key={c} title={already ? `Already quoted ${formatMoney(already)}` : sent ? 'RFQ sent to this supplier' : 'No RFQ sent — a price can still be recorded'}>
            {/* Stacked, not inline: an input and a textarea are inline-block, so in a wide column
                they sat side by side and the price dropped to the baseline of a grown remark
                (Akhil, 2026-10-16: "make the remark look better"). */}
            <div style={{ display: 'grid', gap: 3, width: 120 }}>
            <input
              data-cell={`${colIndex}:${rowIndex}`}
              inputMode="decimal"
              value={text.get(key) ?? ''}
              placeholder={already ? formatMoney(already) : sent ? '' : '—'}
              style={{ ...cellStyle, width: '100%', ...(already ? { background: 'var(--bg)' } : {}) }}
              onChange={(e) => setText((m) => new Map(m).set(key, e.target.value))}
              onBlur={(e) => commit(c, row.line_id, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp') commit(c, row.line_id, (e.target as HTMLInputElement).value)
                move(e, c, rowIndex)
              }}
            />
            {/* The offer remark, under the rate — what the supplier said beyond the number.
                Multi-line (2026-09-18); one line tall until it is being edited (`LineTextarea`),
                so a hundred rows stay a hundred rows. */}
            <LineTextarea
              value={remarks.get(c)?.get(row.line_id) ?? ''}
              placeholder="remark"
              title="Offer remark — MOQ, lead time, brand, ex-stock… filed with this rate"
              style={{ fontSize: 11, textAlign: 'left', padding: '3px 6px', resize: 'none' }}
              onChange={(e) => setRemark(c, row.line_id, e.target.value)}
            />
            </div>
          </td>
        )
      })}
    </tr>
  )
}

function CurrencyCell({ value, onChange }: { value: string; onChange: (currency: string) => void }) {
  return (
    <div style={{ width: 70 }}>
      <LinkCombo value={value} useOptions={useCurrencyPicker} placeholder="AED" onChange={(c) => c && onChange(c)} />
    </div>
  )
}

function NewSupplierInline({ creating, onCreate }: { creating: boolean; onCreate: (name: string, email: string) => void }) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
      <label className="ef" style={{ width: 160 }}>
        <span>New supplier</span>
        <input value={name} placeholder="name" onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="ef" style={{ width: 170 }}>
        <span>Email</span>
        <input value={email} placeholder="optional" onChange={(e) => setEmail(e.target.value)} />
      </label>
      <Button
        size="sm"
        disabled={creating || name.trim() === ''}
        onClick={() => {
          onCreate(name.trim(), email.trim())
          setName('')
          setEmail('')
        }}
      >
        {creating ? 'Creating…' : '+ Create & add column'}
      </Button>
    </div>
  )
}
